// THE MID-RUN STREAM PAYLOADS: what a run reports while it is still working — the tool
// call, the command, the test run, the free-form status, the error and the git op.
// Kept lean — only fields the mothership acts on or surfaces in the UI.
// Split out of `../task-run-event.ts`, which is now the barrel over this directory.

import { z } from 'zod';

export const ToolCallPayloadSchema = z.object({
  tool: z.string().min(1),
  input: z.unknown(),
  output: z.unknown().optional(),
  /** Wall-clock duration in milliseconds. */
  durationMs: z.number().int().nonnegative().optional(),
});

export const CommandPayloadSchema = z.object({
  cmd: z.string().min(1),
  exitCode: z.number().int(),
  stdout: z.string().optional(),
  stderr: z.string().optional(),
  durationMs: z.number().int().nonnegative().optional(),
});
export type CommandPayload = z.infer<typeof CommandPayloadSchema>;

export const TestRunPayloadSchema = z.object({
  framework: z.string().min(1),
  exitCode: z.number().int(),
  passed: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  durationMs: z.number().int().nonnegative().optional(),
  summary: z.string().optional(),
});

export const StatusPayloadSchema = z.object({
  /** e.g. `run_started` | `run_finished` | `run_released` | `heartbeat`. */
  event: z.string().min(1),
  message: z.string().optional(),
});
export type StatusPayload = z.infer<typeof StatusPayloadSchema>;

/**
 * What a box's `bin/setup` refusal was CAUSED by, as the box classified its own
 * evidence. Emitted by the runner on a `gate_unrunnable` setup failure
 * (`apps/runner/src/modes/worker/setup-cause.ts`) and read by the mothership
 * (`apps/worker/src/fleet/setup-cause.ts`) to decide hop vs fail vs park:
 *
 * - `box`     — this box's environment (a missing toolchain, a full disk): another box may pass.
 * - `tree`    — the repository tree at this head (a conflict marker, a broken lockfile):
 *               every box fails the same way, so the branch must change.
 * - `fleet`   — fleet-wide, not this box and not the tree (e.g. registry credentials).
 * - `unknown` — nothing recognised; graded exactly as a box-less refusal always was.
 *
 * ONE definition for producer and consumer: the class is a stored wire value, so a
 * rename is a new class, never a spelling fix.
 */
export const SetupCauseClass = {
  Box: 'box',
  Tree: 'tree',
  Fleet: 'fleet',
  Unknown: 'unknown',
} as const;
export type SetupCauseClass = (typeof SetupCauseClass)[keyof typeof SetupCauseClass];
export const SetupCauseClassSchema = z.enum(SetupCauseClass);

export const SetupCauseSchema = z.object({
  class: SetupCauseClassSchema,
  /** The line that decided the class — one line of testimony, never a log dump. */
  evidence: z.string().max(500).optional(),
});
export type SetupCause = z.infer<typeof SetupCauseSchema>;

export const ErrorPayloadSchema = z.object({
  message: z.string().min(1),
  stack: z.string().optional(),
  /** Optional machine-readable error code. */
  code: z.string().optional(),
  /**
   * The `byok_keys` id of the pool key that funded the failing run (plan 102 slice
   * 07b), so the event sink cools THAT key instead of pausing the account's router
   * backend. Absent on a box-env run and on a runner before 0.0.18.
   */
  key_id: z.string().min(1).optional(),
  /**
   * A RESUMED team attempt's gate count against the one it resumed (2026-09-24): `from` the
   * failures the saved commit on the task's branch carried, `to` the failures this attempt's gate
   * still named. Present only when both are known and non-zero; the mothership reads it
   * through `taskFailureLadder` (`entities/task/gate-progress.ts`). Absent from a runner
   * built before 2026-09-24, which grades exactly as it did.
   *
   * `repeated` (2026-09-28) is `true` exactly when this attempt's gate named the IDENTICAL
   * failure set the saved commit carried — the resumed run re-planned and hit the same wall.
   * A LITERAL `true`, never a `false`: absent is the only other answer, so a runner built
   * before it and a run that moved read the same, and no reader has to tell "not repeated"
   * from "did not say". The mothership stops buying whole-run retries on it
   * (`taskFailureGateRepeated`, `entities/task/gate-progress.ts`).
   *
   * `stage` (2026-09-30) is present when this attempt's red sat at a different gate STAGE
   * than the saved one (`apps/runner` `team/session/wip-stage.ts`): `advanced` — it cleared
   * the legs the save failed on (typecheck-red, then lint-red) — or `regressed` — it fell
   * back to an earlier tier. Then `from` and `to` count different legs of a fail-fast gate,
   * and every reader grades on `stage` instead (`taskFailureGateStage`). Absent ⇒ the counts
   * compare, exactly as before it.
   */
  gate_progress: z
    .object({
      from: z.number().int().positive(),
      to: z.number().int().positive(),
      repeated: z.literal(true).optional(),
      stage: z.enum(['advanced', 'regressed']).optional(),
    })
    .optional(),
  /**
   * The box's class for a `gate_unrunnable` setup refusal ({@link SetupCauseSchema}).
   * Absent from any other error and from a runner built before it, which grades exactly
   * as it did.
   */
  setup_cause: SetupCauseSchema.optional(),
  /**
   * The MODEL on `key_id` the failing run dialled (2026-10-17). A router key fronts
   * several upstream subs, so a dated `provider_rate_limit` parks `(key_id, model)`
   * until its reset (`modelParkOf`, `entities/byok`) while the key's other models keep
   * serving. Bounded like a catalogue row's `model`. Absent from a runner built before
   * it: the consumer then has no model to park and cools the key, as it did.
   */
  model: z.string().min(1).max(200).optional(),
  /**
   * A gate stopped at a memory bound (#7081, `apps/runner` `gate-memory.ts`): `cap_bytes` is
   * the bound that FIRED for `trip` (the per-task cap for `task_cap`, the dz-runner unit's
   * bound for `unit_limit`, the box's RAM for `box_ram`), `observed_bytes` what the runner's
   * watcher read when it shed (`null` for a kernel kill, which reads nothing). The mothership
   * routes a `gate_memory_unmet` retry on it (`gateMemoryFailedCapBytes`,
   * `entities/task/gate-memory-cap.ts`). Absent from a runner built before it, whose message
   * text still names the cap.
   *
   * `.catch(undefined)`: an unreadable member drops the FIELD, never the error. A runner that
   * ships a new `trip` before the mothership deploys would otherwise fail the whole payload,
   * which grades `error_unclassified` and is TERMINAL — a task lost over a routing hint.
   */
  gate_memory: z
    .object({
      cap_bytes: z.number().int().positive(),
      observed_bytes: z.number().int().nonnegative().nullable(),
      trip: z.enum(['task_cap', 'unit_limit', 'box_ram']),
      killed_by: z.enum(['runner', 'kernel']),
    })
    .optional()
    .catch(undefined),
});

export const GitOpPayloadSchema = z.object({
  /** e.g. `commit` | `push` | `pr_create` | `branch_create`. */
  op: z.string().min(1),
  ref: z.string().optional(),
  url: z.string().url().optional(),
});
